import type { BetterstartIntegrationId } from '@cli-types/integration-engine/integrations.js'
import type {
  InstallIntegrationsOptions,
  InstallIntegrationsResult
} from '@cli-types/next-adapter/runtime.js'
import { unique } from '@cli-utils/shared.js'
import { writeConfigFile } from '@core-engine/config/serialize/write-config-file.js'
import { integrationManifestStore } from '@integration-engine/manifests/integration-manifest-store.js'
import { installDependencyDelta } from '@next-adapter/installable-runtime/install-dependency-delta.js'
import { getIntegrationDefinition } from '@next-adapter/integration-registry.js'
import { activateConfiguredProviderIntegration } from './activate-configured-provider-integration.js'
import { assertNoIntegrationConflicts } from './assert-no-integration-conflicts.js'
import { buildInstalledIntegrationManifest } from './build-installed-integration-manifest.js'
import { configureIntegration } from './configure-integration.js'
import { ensureEmailTemplatesDir } from './ensure-email-templates-dir.js'
import { ensureIntegrationRuntimeSupport } from './ensure-integration-runtime-support.js'
import { integrationFiles } from './integration-files.js'
import { isProviderIntegration } from './is-provider-integration.js'
import { normalizeInstalledIntegrations } from './normalize-installed-integrations.js'
import { resolveIntegrationInstallOrder } from './resolve-integration-install-order.js'
import { writeCoreProviderIndexes } from './write-core-provider-indexes.js'

export async function installIntegrations({
  cwd,
  config,
  pm,
  integrationIds,
  interactive,
  includeLinter,
  dependenciesInstalled = false
}: InstallIntegrationsOptions): Promise<InstallIntegrationsResult> {
  const orderedIntegrationIds = resolveIntegrationInstallOrder(integrationIds)
  const installedIntegrationIds = normalizeInstalledIntegrations(config)
  assertNoIntegrationConflicts(orderedIntegrationIds, installedIntegrationIds)
  const skipped: BetterstartIntegrationId[] = []
  const installed: BetterstartIntegrationId[] = []
  const activated: BetterstartIntegrationId[] = []
  const warnings: string[] = []

  for (const integrationId of orderedIntegrationIds) {
    const definition = getIntegrationDefinition(integrationId)

    if (installedIntegrationIds.includes(integrationId)) {
      if (!isProviderIntegration(definition)) {
        skipped.push(integrationId)
        continue
      }

      const previousManifest = integrationManifestStore.load(cwd, integrationId)
      const configureResult = await configureIntegration(definition, cwd, interactive)
      ensureIntegrationRuntimeSupport(cwd, config, definition)

      if (!configureResult.configured) {
        integrationManifestStore.save(
          cwd,
          buildInstalledIntegrationManifest({
            config,
            definition,
            configured: false,
            previousManifest
          })
        )
        skipped.push(integrationId)
        warnings.push(
          `${integrationId} is installed but was not activated because its required environment variables are still missing.`
        )
        continue
      }

      const providerChanged = activateConfiguredProviderIntegration(definition, config)

      if (definition.capabilities?.emailProvider) {
        ensureEmailTemplatesDir(cwd, config)
      }
      writeCoreProviderIndexes(cwd, config)
      integrationManifestStore.save(
        cwd,
        buildInstalledIntegrationManifest({
          config,
          definition,
          configured: true,
          previousManifest
        })
      )
      writeConfigFile(cwd, config)

      if (providerChanged || previousManifest?.configured !== true) {
        activated.push(integrationId)
      } else {
        skipped.push(integrationId)
      }
      continue
    }

    integrationFiles.ensureDirectFileTargetsAvailable(cwd, config, definition)

    const configureResult = await configureIntegration(definition, cwd, interactive)
    ensureIntegrationRuntimeSupport(cwd, config, definition)
    await installDependencyDelta({
      cwd,
      pm,
      installableId: integrationId,
      includeLinter,
      dependenciesInstalled,
      next: {
        presetIds: config.presets.installed,
        integrationIds: [...installedIntegrationIds, integrationId]
      },
      current: {
        presetIds: config.presets.installed,
        integrationIds: installedIntegrationIds
      }
    })

    const writtenFiles = integrationFiles.writeDirectFiles(cwd, config, definition)

    installedIntegrationIds.push(integrationId)
    config.integrations.installed = unique(installedIntegrationIds).sort()

    if (configureResult.configured) {
      activateConfiguredProviderIntegration(definition, config)
    }

    if (definition.capabilities?.emailProvider) {
      ensureEmailTemplatesDir(cwd, config)
    }
    writeCoreProviderIndexes(cwd, config)

    const manifest = buildInstalledIntegrationManifest({
      config,
      definition,
      writtenFiles,
      configured: configureResult.configured
    })

    integrationManifestStore.save(cwd, manifest)
    writeConfigFile(cwd, config)
    installed.push(integrationId)

    if (!configureResult.configured && isProviderIntegration(definition)) {
      warnings.push(
        `${integrationId} was installed but not activated because its required environment variables are still missing.`
      )
    }
  }

  return {
    installed,
    activated,
    skipped,
    warnings,
    config
  }
}
